import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { CaretLeft, X } from '@hungryroot/icons';
import { Button, IconButton } from '@hungryroot/ramen';
import { ArchingProgressBar } from '../../ArchingProgressBar/ArchingProgressBar';
import { type useDetectChanges } from '../useDetectChanges';
import { Stage, type useFoodFeedbackModal } from '../useFoodFeedbackModal';
import {
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';
import { RatableContent } from './RatableContent';
import styles from './RatingStage.module.css';
import sharedStyles from '../shared.module.css';

interface RatingStageProps {
  products: ReturnType<typeof useFoodFeedbackModal>['products'];
  pairings: ReturnType<typeof useFoodFeedbackModal>['pairings'];
  prevStage?: () => void;
  dismissModal: () => void;
  nextStage?: () => void;
  stage: Stage;
  changeDetection: ReturnType<typeof useDetectChanges>;
  onRating?: ({
    item,
    rating,
  }: {
    item: RatablePairingItem | RatableProductItem;
    rating: boolean | null;
  }) => void;
  ratedItemsState: {
    ratedItemsCount: number;
    setRatedItemsCount: (prev: (count: number) => number) => void;
  };
  totalItemsCount: number;
}

export function RatingStage(props: RatingStageProps) {
  const {
    pairings,
    products,
    prevStage,
    nextStage,
    dismissModal,
    stage,
    changeDetection,
    onRating,
    ratedItemsState: { ratedItemsCount, setRatedItemsCount },
    totalItemsCount,
  } = props;

  const { ref: pairingContentRef, inView: pairingContentInView } = useInView();
  const { ref: productContentRef, inView: productContentInView } = useInView();

  const handleOnRating = (
    prevValue: boolean | null,
    newValue: boolean | null
  ) => {
    if (prevValue !== newValue) {
      if (prevValue === null) {
        setRatedItemsCount((prevCount) => prevCount + 1);
      } else if (newValue === null) {
        setRatedItemsCount((prevCount) => prevCount - 1);
      }
    }
  };

  return (
    <div
      className={clsx(
        sharedStyles['visibility-transition'],
        styles['rating-stage']
      )}
      data-visible={
        stage === Stage.RatablePairings ||
        stage === Stage.RatableProducts ||
        null
      }
    >
      <div
        className={clsx(sharedStyles.header, styles['rating-stage-header'])}
        data-scrolled={
          (stage === Stage.RatablePairings && !pairingContentInView) ||
          (stage === Stage.RatableProducts && !productContentInView) ||
          null
        }
      >
        <IconButton
          className={clsx({
            [sharedStyles['prev-stage-button']]: true,
            [sharedStyles['show-prev-stage-button']]: Boolean(prevStage),
          })}
          style="muted"
          prominence="tertiary"
          size="medium"
          icon={<CaretLeft />}
          onClick={prevStage}
          ariaLabel="Previous stage"
        />
        <ArchingProgressBar
          value={ratedItemsCount}
          maxValue={totalItemsCount}
          aria-label="Number of ratings completed"
        />
        <IconButton
          style="muted"
          prominence="tertiary"
          size="medium"
          icon={<X />}
          onClick={dismissModal}
          ariaLabel="Dismiss modal"
        />
      </div>

      <RatableContent
        testId="ratable-pairings-container"
        changeDetection={changeDetection}
        intersectionRef={pairingContentRef}
        items={pairings}
        visible={stage === Stage.RatablePairings}
        onRating={({ prevValue, newValue, item }) => {
          onRating?.({ rating: newValue, item });
          handleOnRating(prevValue, newValue);
        }}
        heading="How were your recipes?"
      />

      <RatableContent
        testId="ratable-products-container"
        changeDetection={changeDetection}
        intersectionRef={productContentRef}
        items={products}
        visible={stage === Stage.RatableProducts}
        onRating={({ prevValue, newValue, item }) => {
          onRating?.({ rating: newValue, item });
          handleOnRating(prevValue, newValue);
        }}
        heading="How were your groceries?"
      />

      <div className={styles.footer}>
        <Button
          className={styles['continue-button']}
          onClick={nextStage}
          prominence="primary"
          style="default"
          size="medium"
          centerContent={true}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
